import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { Card, Modal, Form } from "react-bootstrap";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { security_key, BASE_URL } from "../global";

// Diagnostics app base + path
const DIAG_APP = (process.env.REACT_APP_DIAGNOSTICS_APP_URL || "http://localhost:5173").replace(/\/+$/, "");
const STUDENT_PATH = (process.env.REACT_APP_DIAGNOSTICS_STUDENT_PATH || "/diagnostics/welcome").replace(/\/+$/, "");

function safeCopyToClipboard(text) {
    // Modern clipboard API (requires https or localhost)
    if (navigator?.clipboard?.writeText) {
        return navigator.clipboard.writeText(text);
    }
    // Fallback
    return new Promise((resolve, reject) => {
        try {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.setAttribute("readonly", "");
            ta.style.position = "absolute";
            ta.style.left = "-9999px";
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand("copy");
            document.body.removeChild(ta);
            ok ? resolve() : reject(new Error("copy_failed"));
        } catch (e) {
            reject(e);
        }
    });
}

function isValidEmail(email) {
    const e = (email || "").trim();
    // simple, practical validation
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export default function DiagnosticsStudents() {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const hasRows = rows.length > 0;

    const [showLinkModal, setShowLinkModal] = useState(false);
    const [activeStudent, setActiveStudent] = useState(null);
    const [generatedLink, setGeneratedLink] = useState("");
    const [expiryDays, setExpiryDays] = useState(null);
    const [busyGenerate, setBusyGenerate] = useState(false);

    // NEW: email send UI state
    const [toEmail, setToEmail] = useState("");
    const [busySendEmail, setBusySendEmail] = useState(false);
    const [lastSentTo, setLastSentTo] = useState(null);

    const centreKey = sessionStorage.getItem("key");

    const fetchStudents = useCallback(async () => {
        if (!centreKey) {
            toast.error("Missing centre key in sessionStorage.");
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key,
                mode: "diagnostics_list_students",
                fk_tenat_key: centreKey,
            });

            if (res?.data?.success) {
                setRows(res.data.data || []);
            } else {
                toast.error(res?.data?.error || "Failed to load diagnostics students.");
                setRows([]);
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to load diagnostics students.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [centreKey]);

    useEffect(() => {
        fetchStudents();
    }, [fetchStudents]);

    const closeModal = () => {
        setShowLinkModal(false);
        setToEmail("");
        setBusySendEmail(false);
        setLastSentTo(null);
    };

    const generateStudentLink = async (student) => {
        if (!centreKey) {
            toast.error("Missing centre key in sessionStorage.");
            return;
        }
        if (!student?.user_key) {
            toast.error("Missing student user_key.");
            return;
        }

        setBusyGenerate(true);
        setActiveStudent(student);
        setGeneratedLink("");
        setToEmail("");
        setLastSentTo(null);

        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key,
                mode: "diagnostics_generate_token",
                fk_tenat_key: centreKey,
                user_key: student.user_key,
                purpose: "student",
            });

            const ok = !!res?.data?.success;
            const token = res?.data?.token ?? res?.data?.data?.token;

            if (!ok || !token) {
                toast.error(res?.data?.error || "Could not generate student token.");
                return;
            }

            const expSeconds = Number(res?.data?.exp_seconds ?? res?.data?.data?.exp_seconds);
            setExpiryDays(expSeconds > 0 ? Math.round(expSeconds / 86400) : null);

            const link = `${DIAG_APP}${STUDENT_PATH}?token=${encodeURIComponent(token)}`;
            setGeneratedLink(link);
            setShowLinkModal(true);
        } catch (e) {
            console.error(e);
            toast.error("Could not generate student token.");
        } finally {
            setBusyGenerate(false);
        }
    };

    // Send email via backend (backend generates a fresh student token + sends it)
    const sendStudentLinkEmail = async () => {
        if (!centreKey) {
            toast.error("Missing centre key in sessionStorage.");
            return;
        }
        if (!activeStudent?.user_key) {
            toast.error("Missing student user_key.");
            return;
        }

        const email = (toEmail || "").trim();
        if (!isValidEmail(email)) {
            toast.error("Please enter a valid email address.");
            return;
        }

        setBusySendEmail(true);
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key,
                mode: "diagnostics_send_student_link_email",
                fk_tenat_key: centreKey,
                user_key: activeStudent.user_key,
                to_email: email,
            });

            if (res?.data?.success) {
                // backend returns data.link (useful to show the exact emailed link)
                const emailedLink = res?.data?.data?.link;
                if (emailedLink) setGeneratedLink(emailedLink);

                setLastSentTo(email);
                toast.success(`Email sent to ${email}`);
            } else {
                toast.error(res?.data?.message || res?.data?.error || "Failed to send email.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to send email.");
        } finally {
            setBusySendEmail(false);
        }
    };

    const columns = useMemo(
        () => [
            { field: "user_name", headerName: "Student Name", flex: 1, minWidth: 200 },
            {
                field: "registered_level_name",
                headerName: "Registered Level",
                flex: 1,
                minWidth: 180,
                valueGetter: (params) => params.row.registered_level_name || params.row.registered_level_id || "-",
            },
            {
                field: "user_year_level_name",
                headerName: "Year Level",
                flex: 1,
                minWidth: 180,
                valueGetter: (params) => params.row.user_year_level_name || params.row.user_year_level || "-",
            },
            {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                minWidth: 180,
                renderCell: (params) => (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => generateStudentLink(params.row)}
                        disabled={busyGenerate}
                    >
                        {busyGenerate ? "Generating..." : "Generate Link"}
                    </Button>
                ),
            },
        ],
        [busyGenerate]
    );

    const canSendEmail = isValidEmail(toEmail) && !!activeStudent?.user_key;

    return (
        <>
            <ToastContainer position="top-center" />

            <div className="page-wrapper">
                <div className="mb-3 d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="page-title">Diagnostics Students</h5>
                        <div role="presentation">
                            <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
                                <NavLink to="/calendar">Home</NavLink>
                                <Typography color="text.primary">Diagnostics Students</Typography>
                            </Breadcrumbs>
                        </div>
                    </div>

                    <div className="d-flex gap-2">
                        <Button variant="contained" className="primary-filled" onClick={fetchStudents}>
                            Refresh
                        </Button>
                    </div>
                </div>

                <Card className="my-card p-2 bg-white">
                    <Card.Body>
                        <Box sx={{
                            height: hasRows ? "auto" : 200,
                            maxHeight: "calc(100vh - 270px)",
                            width: "100%"
                        }}>
                            <DataGrid
                                loading={loading}
                                slots={{
                                    loadingOverlay: LinearProgress,
                                    toolbar: GridToolbar,
                                }}
                                sx={{
                                    maxHeight: "calc(100vh - 270px)",
                                }}
                                slotProps={{
                                    toolbar: { showQuickFilter: true },
                                }}
                                rows={(rows || []).map((r, idx) => ({ ...r, id: r.user_key || `row-${idx}` }))}
                                columns={columns}
                                pagination={false}
                                hideFooterPagination
                                hideFooter
                                disableRowSelectionOnClick
                                showCellVerticalBorder
                                showColumnVerticalBorder
                            />
                        </Box>
                    </Card.Body>
                </Card>

                <Modal show={showLinkModal} onHide={closeModal} centered size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Student Link</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <div className="mb-2">
                            <strong>Student:</strong> {activeStudent?.user_name || "-"}
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Share this link with parents</Form.Label>
                            <Form.Control type="text" value={generatedLink} readOnly />
                            <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
                                Token expiry: <strong>{expiryDays ? `${expiryDays} days` : "—"}</strong>
                            </div>
                        </Form.Group>

                        <hr />

                        <Form.Group className="mb-2">
                            <Form.Label>Send link via email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="parent@example.com"
                                value={toEmail}
                                onChange={(e) => setToEmail(e.target.value)}
                                disabled={busySendEmail}
                            />
                            {lastSentTo ? (
                                <div className="mt-2 text-success" style={{ fontSize: 12 }}>
                                    Sent to <strong>{lastSentTo}</strong>
                                </div>
                            ) : (
                                <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
                                    The email will include the link{expiryDays ? `, which expires in ${expiryDays} days` : ""}.
                                </div>
                            )}
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="contained" className="primary-gray" onClick={closeModal} disabled={busySendEmail}>
                            Close
                        </Button>

                        <Button
                            variant="contained"
                            className="primary-gray"
                            onClick={async () => {
                                try {
                                    await safeCopyToClipboard(generatedLink);
                                    toast.success("Link copied to clipboard!");
                                } catch (e) {
                                    toast.error("Could not copy link.");
                                }
                            }}
                            disabled={!generatedLink || busySendEmail}
                        >
                            Copy Link
                        </Button>

                        <Button
                            variant="contained"
                            className="primary-filled"
                            onClick={sendStudentLinkEmail}
                            disabled={!canSendEmail || busySendEmail}
                        >
                            {busySendEmail ? "Sending..." : "Send Email"}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
}
